import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import QuestionLayout, { QUESTION_MODES } from '../../features/questions/Question/QuestionLayout';
import QuestionDisplay, { QUESTION_TYPES } from '../../features/questions/Question/components/QuestionDisplay';
import HiraganaLoader from '../../components/HiraganaLoader';
import AskMinaChat from '../../features/chat/AskMinaChat'
import { sendQuestionChat } from '../../api/exams';
import '../../styles/contests/ListeningMaster.css';
import { API_URL } from '../../app/constants'


function ModernReviewQuestion() {
  const navigate = useNavigate();
  const location = useLocation();
  const { exam_id } = useParams();
  const [currentContentIndex, setCurrentContentIndex] = useState(location.state?.currentContentIndex || 0);
  const [examData, setExamData] = useState(location.state?.examData || []);
  const [showChat, setShowChat] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [bookmarkedQuestions, setBookmarkedQuestions] = useState(new Set());
  
  // Load saved questions to show bookmark status (read-only)

  const checkSavedQuestions = async () => {
    try {
      const jwt = localStorage.getItem('jwt');
      const response = await fetch(`${API_URL}/api/v1/panel/saved-questions`, {
        headers: { 'Authorization': `Bearer ${jwt}` },
        method: 'GET'
      });
      
      if (response.ok) {
        const data = await response.json();
        const savedQuestionIds = new Set(data.map(q => q.question_id));
        setBookmarkedQuestions(savedQuestionIds);
        console.log('Loaded saved questions:', savedQuestionIds);
      } else {
        console.error('Error loading saved questions');
      }
    } catch (error) {
      console.error('Error checking saved questions:', error);
    }
  };

  // Handle bookmark toggle - READ ONLY in review mode
  const handleBookmarkToggle = (questionId) => {
    // Do nothing - bookmarks are read-only in review mode
    console.log('Bookmark is read-only in review mode');
  };
  
  
  // Check for saved questions on component load
  useEffect(() => {
    checkSavedQuestions();
  }, []);
  
  // If we don't have script_image data in the passed state, fetch it directly from the API
  useEffect(() => {
    // Check if examData is empty or doesn't have script_image data
    const needsImageData = examData.length > 0 && 
                       (!examData[currentContentIndex]?.script_image || 
                        !examData[currentContentIndex]?.script_image?.urls);
                        
    // Only fetch if we have exam_id and need image data
    if (exam_id && needsImageData && !originalData) {
      const fetchOriginalData = async () => {
        try {
          setLoading(true);
          const jwt = localStorage.getItem('jwt');
          
          // Use the appropriate endpoint based on exam_id
          let apiUrl;
          if (exam_id === 'diagnostic') {
            apiUrl = `${API_URL}/api/v1/panel/diagnostic/user/responses`;
          } else if (exam_id === 'mock-exam') {
            apiUrl = `${API_URL}/api/v1/mock-exam/user/responses`;
          } else {
            apiUrl = `${API_URL}/api/v1/panel/personalized-exam/${exam_id}`;
          }
          
          const response = await fetch(apiUrl, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${jwt}`, 'Content-Type': 'application/json' },
          });
          
          const data = await response.json();
          console.log('Direct API fetch response:', data);
          
          // Save the original data
          setOriginalData(data);
          
          // Update examData with script_image for ALL questions
          if (data.responses && Array.isArray(data.responses) && data.responses.length > 0) {
            console.log('Updating all questions with script_image data from API');
            
            // Update ALL questions with their script_image data
            const updatedExamData = examData.map(question => {
              const originalQuestion = data.responses.find(q => q.question_id === question.question_id);
              
              if (originalQuestion && originalQuestion.script_image) {
                console.log(`Found script_image for question ${question.question_id}:`, originalQuestion.script_image);
                return {
                  ...question,
                  script_image: originalQuestion.script_image
                };
              }
              
              return question;
            });
            
            setExamData(updatedExamData);
            console.log('Updated examData with script_image for all questions');
          }
          
          setLoading(false);
        } catch (error) {
          console.error('Error fetching original data:', error);
          setError('Failed to fetch image data');
          setLoading(false);
        }
      };
      
      fetchOriginalData();
    }
  }, [exam_id, examData, currentContentIndex, originalData]);

  // Handle loading, error, and no data states
  if (loading) {
    return <HiraganaLoader />;
  }
  
  if (error) {
    return <div className="loading-container"><div>{error}</div></div>;
  }
  
  if (examData.length === 0 || !examData[currentContentIndex]) {
    return <div className="loading-container"><div>No content available</div></div>;
  }

  const totalQuestions = examData.length;
  const currentQuestion = examData[currentContentIndex];
  const hasScript = currentQuestion?.script && currentQuestion.script !== "0";

  // Debug logging for image data
  console.log("ModernReviewQuestion - Current question:", currentQuestion);
  console.log("ModernReviewQuestion - Has script image:", currentQuestion?.script_image ? "Yes" : "No");
  if (currentQuestion?.script_image) {
    console.log("ModernReviewQuestion - Script image full data:", JSON.stringify(currentQuestion.script_image));
  }
  console.log("ModernReviewQuestion - Has imageUrl:", currentQuestion?.imageUrl ? "Yes" : "No");

  // Log details about the image sources
  const imageSource = {};
  if (currentQuestion?.imageUrl) {
    imageSource.explicitImageUrl = currentQuestion.imageUrl;
  }
  if (currentQuestion?.script_image) {
    console.log("ModernReviewQuestion - Script image data:", currentQuestion.script_image);
    if (currentQuestion.script_image.urls && currentQuestion.script_image.urls.length > 0) {
      imageSource.scriptImageUrls = currentQuestion.script_image.urls[0];
      console.log("ModernReviewQuestion - Image URL from script_image:", currentQuestion.script_image.urls[0]);
    }
  }
  if (currentQuestion?.script_image_url) {
    imageSource.scriptImageUrl = currentQuestion.script_image_url;
  }
  if (currentQuestion?.related && Array.isArray(currentQuestion.related) && currentQuestion.related.length > 0) {
    imageSource.relatedPath = `/images/${currentQuestion.level || "N5"}/reading/script/${currentQuestion.related[0]}.png`;
  }
  if (currentQuestion?.ref_id) {
    imageSource.refIdPath = `/images/${currentQuestion.level || "N5"}/reading/script/${currentQuestion.ref_id}.png`;
  }

  console.log("ModernReviewQuestion - Available image sources:", imageSource);

  const handlePreviousQuestion = () => {
    if (currentContentIndex > 0) {
      // Close the chat if it's open when changing questions
      setShowChat(false);
      setCurrentContentIndex(currentContentIndex - 1);
    }
  };

  const handleNextQuestion = () => {
    if (currentContentIndex < totalQuestions - 1) {
      // Close the chat if it's open when changing questions
      setShowChat(false);
      setCurrentContentIndex(currentContentIndex + 1);
    } else {
      // If last question, go back to the review page
      handleBackButtonClick();
    }
  };

  const handleBackButtonClick = () => {
    // Special handling for diagnostic exam
    if (exam_id === 'diagnostic') {
      navigate('/review-personalized/diagnostic');
    } else {
      navigate(`/review-personalized/${exam_id}`);
    }
  };

  const handleAskMina = () => {
    setShowChat(true);
  };

  const handleCloseChat = () => {
    setShowChat(false);
  };

  // Custom API handler for review context
  const handleReviewSendMessage = async ({ userInput, questionContext, conversationHistory }) => {
    try {
      // Find user's selected answer
      let userAnswer = currentQuestion.selected_answer;
      
      // Prepare question context for the API - using the same format as PersonalizedExam.js
      const apiQuestionContext = {
        question_id: currentQuestion?.question_id,
        question_text: currentQuestion?.question_text || currentQuestion?.question,
        options: currentQuestion?.choices,
        correct_answer: currentQuestion?.correct_answer,
        explanation: currentQuestion?.explanation,
        user_answer: userAnswer, // Add the user's answer
        image_path: currentQuestion?.script_image_url || 
                  (currentQuestion?.script_image?.urls?.[0]) ||
                  (currentQuestion.related && Array.isArray(currentQuestion.related) && currentQuestion.related.length > 0 ?
                    `/images/${currentQuestion.level || "N5"}/reading/script/${currentQuestion.related[0]}.png` :
                    (currentQuestion.ref_id ?
                      `/images/${currentQuestion.level || "N5"}/reading/script/${currentQuestion.ref_id}.png` :
                      null))
      };
      
      console.log('Review - Question context:', apiQuestionContext);
      
      // Use the sendQuestionChat API with the same parameters as in PersonalizedExam.js
      const responseData = await sendQuestionChat(apiQuestionContext, userInput, conversationHistory);
      console.log('Review - API response:', responseData);
      
      // Extract the bot's response text - handle different response formats
      let botResponseText;
      
      if (typeof responseData === 'string') {
        const jsonStartIndex = responseData.indexOf('{');
        if (jsonStartIndex > 0) {
          botResponseText = responseData.substring(0, jsonStartIndex).trim();
        } else {
          botResponseText = responseData;
        }
      } else if (responseData.choices && responseData.choices[0] && responseData.choices[0].message) {
        botResponseText = responseData.choices[0].message.content;
      } else if (responseData.response) {
        botResponseText = responseData.response;
      } else if (responseData.text) {
        botResponseText = responseData.text;
      } else if (responseData.history) {
        const systemMessages = responseData.history.filter(msg => msg.role === 'system');
        if (systemMessages.length > 0) {
          botResponseText = systemMessages[systemMessages.length - 1].content;
        } else {
          botResponseText = "I'm sorry, I couldn't process your question properly.";
        }
      } else {
        botResponseText = "I'm sorry, but I couldn't process your question properly. Please try asking something else.";
      }
      
      return botResponseText;
      
    } catch (error) {
      console.error('Review - Error processing chat message:', error);
      return 'I apologize, but I encountered an error while processing your question. Please try again later.';
    }
  };

  const formatText = (text) => {
    if (!text || text === '0') return '';
    return text.replace(/{{NEWLINE}}/g, '\n');
  };

  // Format the answers and question for QuestionDisplay
  let questionType = QUESTION_TYPES.SINGLE;
  let options = [];
  let questions = [];

  // Check if this question has a questions array (which would indicate a group of related questions)
  const hasQuestionsArray = currentQuestion.questions && Array.isArray(currentQuestion.questions) && currentQuestion.questions.length > 0;

  // Check for explicit isRelatedGroup property added by ReviewPersonalized
  const isRelatedGroup = currentQuestion.isRelatedGroup === true || hasQuestionsArray;

  // Use either the questions array or related questions array if available
  const relatedQuestions = hasQuestionsArray ? currentQuestion.questions :
                         (isRelatedGroup && currentQuestion.relatedQuestions ? currentQuestion.relatedQuestions : []);

  console.log("Is related group:", isRelatedGroup);
  console.log("Has questions array:", hasQuestionsArray);
  console.log("Related questions:", relatedQuestions);
  console.log("Current question script:", currentQuestion.script);

  // Check for explicitly provided image URL
  const scriptImageUrl = currentQuestion.imageUrl ||
                       (currentQuestion.script_image && currentQuestion.script_image.urls && currentQuestion.script_image.urls.length > 0 ?
                          currentQuestion.script_image.urls[0] : null);

  // Check if this is likely a reading question set
  const hasReadingScript = hasScript ||
                         (currentQuestion.ref_id && currentQuestion.script_image) ||
                         (currentQuestion.ref_id && currentQuestion.script_image_url) ||
                         (currentQuestion.ref_id && currentQuestion.related) ||
                         scriptImageUrl;

  console.log("Has reading script:", hasReadingScript);

  // First, check if this is a related question group or a script-based group
  const isMultipleQuestions = (isRelatedGroup ||
                            hasQuestionsArray ||
                            relatedQuestions.length > 1 ||
                            hasScript ||
                            hasReadingScript);

  console.log("Using multiple questions:", isMultipleQuestions);

  if (isMultipleQuestions) {
    // Multiple questions with a reading passage or related group
    questionType = QUESTION_TYPES.MULTIPLE;

    // Get all questions - either from current.questions, relatedQuestions, or examData
    const allQuestions = Array.isArray(currentQuestion.questions) ? currentQuestion.questions :
                        (relatedQuestions.length > 0 ? relatedQuestions :
                        [currentQuestion]); // Fallback to just the current question

    console.log("Available questions for mapping:", allQuestions);

    // Map all related questions to the format expected by QuestionDisplay
    questions = allQuestions.map(q => ({
      id: q.question_id,
      text: q.question_text || q.question,
      ref_id: q.ref_id,
      options: [
        ...(q.choices || []).map((choice, index) => ({ id: index, text: choice }))
      ],
      correctAnswer: q.correct_answer,
      explanation: q.explanation
    }));

    // Sort questions by ref_id if possible
    questions.sort((a, b) => {
      if (a.ref_id && b.ref_id) {
        return parseInt(a.ref_id, 10) - parseInt(b.ref_id, 10);
      }
      return 0;
    });

    console.log("Final mapped questions:", questions);
  } else {
    // Single question
    questionType = QUESTION_TYPES.SINGLE;
    options = [
      ...(currentQuestion.choices || []).map((choice, index) => ({ id: index, text: choice }))
    ];
  }

  // For review, create a map to store the selected answers
  const selectedAnswers = {};

  // Find the user's selected answer index for the current question
  const userSelectedAnswerIndex = currentQuestion.choices?.findIndex(
    choice => choice === currentQuestion.selected_answer
  );

  // Find the correct answer index for the current question
  const correctAnswerIndex = currentQuestion.choices?.findIndex(
    choice => choice === currentQuestion.correct_answer
  );

  // PER USER'S REQUEST: Only show the user's selected answers if they exist
  // Otherwise, ONLY show correct answers in green and don't mark anything as incorrect
  console.log("User selected answer index:", userSelectedAnswerIndex);
  console.log("Correct answer index:", correctAnswerIndex);

  // Check if we have an actual user answer from the API
  const hasUserAnswer = userSelectedAnswerIndex !== -1 && userSelectedAnswerIndex !== undefined;

  if (hasUserAnswer) {
    // If we have a user-selected answer, show it (it will appear in red if it's wrong)
    selectedAnswers[currentQuestion.question_id || 0] = userSelectedAnswerIndex;
    selectedAnswers[0] = userSelectedAnswerIndex;
    console.log(`Using actual user selected answer: ${userSelectedAnswerIndex}`);
  } else {
    // If we don't have a user answer, don't set any selected answer
    // This will only show the correct answer in green without highlighting anything in red
    console.log(`No user answer data available - only showing correct answer in green`);
    // Intentionally NOT setting any selected answer here
  }

  // Check if we have the allSelectedAnswers field from the enhanced data structure
  if (currentQuestion.allSelectedAnswers && Object.keys(currentQuestion.allSelectedAnswers).length > 0) {
    console.log("Using allSelectedAnswers from enhanced data structure:", currentQuestion.allSelectedAnswers);

    // For each question in the group, find its selected answer index
    questions.forEach(question => {
      const questionId = question.id;
      const selectedAnswer = currentQuestion.allSelectedAnswers[questionId];

      if (selectedAnswer) {
        // Find the index of this selected answer in the choices array
        const choices = examData.find(q => q.question_id === questionId)?.choices || [];
        const answerIndex = choices.findIndex(choice => choice === selectedAnswer);

        if (answerIndex >= 0) {
          selectedAnswers[questionId] = answerIndex;
        }
      }
    });
  }
  // Fallback: Store the selected answers by searching through all questions
  else if (isMultipleQuestions && questions.length > 0) {
    questions.forEach(question => {
      // For each question in the group, try to find its selected answer
      const q = examData.find(item => item.question_id === question.id);
      if (q && q.selected_answer) {
        // Only if we have an actual user-selected answer, use it
        const qSelectedIndex = q.choices?.findIndex(choice => choice === q.selected_answer);
        if (qSelectedIndex !== undefined && qSelectedIndex >= 0) {
          console.log(`Using real user selected answer for question ${question.id}: ${qSelectedIndex}`);
          selectedAnswers[question.id] = qSelectedIndex;
        } else {
          console.log(`User selected answer found for question ${question.id} but couldn't find its index in choices`);
          // Don't set a value if we can't find the index - only show the correct answer in green
        }
      } else {
        console.log(`No selected answer data available for question ${question.id}`);
        // Don't set any answer as selected - only show the correct answer in green
      }
    });
  }

  console.log("Final selectedAnswers map:", selectedAnswers);

  return (
    <>
      <QuestionLayout
        timer=""
        totalQuestions={totalQuestions}
        currentQuestion={currentContentIndex + 1}
        onExit={handleBackButtonClick}
        isAnswered={true} // Always answered in review mode
        mode={QUESTION_MODES.REVIEW}
        footer={
          <>
            <button 
              className="ask-mina-btn" 
              onClick={handleAskMina}
            >
              Ask Mina
            </button>
            
            <div className="navigation-buttons">
              <button 
                className="nav-btn previous" 
                disabled={currentContentIndex === 0}
                onClick={handlePreviousQuestion}
              >
                Previous
              </button>
              
              <button 
                className="nav-btn next" 
                onClick={handleNextQuestion}
              >
                {currentContentIndex === totalQuestions - 1 ? 'Finish' : 'Next'}
              </button>
            </div>
          </>
        }
      >
        <QuestionDisplay
          type={questionType}
          title={`Question ${currentContentIndex + 1}`}
          instruction={currentQuestion.instruction}
          script={null}
          questionText={currentQuestion.question_text || currentQuestion.question}
          readingText={hasScript ? currentQuestion.script : null}
          questions={questions}
          options={options}
          selectedAnswer={selectedAnswers[0]} // Use the fake selected index if needed
          selectedAnswers={selectedAnswers}
          isAnswered={true} // Always answered in review mode
          correctAnswer={correctAnswerIndex} // Use the index, not the string value
          mode={QUESTION_MODES.REVIEW} // Use the constant instead of string literal
          answersHistory={{ // Add history to show selected answers
            [currentQuestion.question_id]: {
              selectedAnswers: selectedAnswers,
              isCorrect: false // Mark it wrong to show red
            }
          }}
          explanation={currentQuestion.explanation}
          formatText={formatText}
          imageUrl={currentQuestion?.script_image?.urls?.[0] ||
                    currentQuestion?.script_image_url ||
                    (currentQuestion.related && Array.isArray(currentQuestion.related) && currentQuestion.related.length > 0 ?
                      `/images/${currentQuestion.level || "N5"}/reading/script/${currentQuestion.related[0]}.png` :
                      (currentQuestion.ref_id ?
                        `/images/${currentQuestion.level || "N5"}/reading/script/${currentQuestion.ref_id}.png` :
                        null))
                  }
          currentQuestionId={currentQuestion.question_id}
          isBookmarked={bookmarkedQuestions.has(currentQuestion.question_id)}
          onBookmarkToggle={handleBookmarkToggle}
        />
      </QuestionLayout>

      {/* Ask Mina Chat Component */}
      <AskMinaChat
        visible={showChat}
        onClose={handleCloseChat}
        questionContext={currentQuestion}
        questionSet={relatedQuestions.length > 0 ? relatedQuestions : [currentQuestion]}
        answeredQuestions={{ [currentQuestion.question_id]: currentQuestion.selected_answer }}
        resetOnClose={false}
        onSendMessage={handleReviewSendMessage}
      />
    </>
  );
}

export default ModernReviewQuestion;